import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, Chapter, scrollToId } from "./Reveal";

const PLANS = [
  {
    name: "Launch",
    tag: "For destination businesses starting their digital journey.",
    features: ["Branded storefront on your domain", "Destination catalogue & packages", "Inquiry management", "Quotation engine"],
    featured: false,
    tid: "pricing-cta-launch",
  },
  {
    name: "Grow",
    tag: "For businesses managing larger catalogues, enquiries, packages and quotations.",
    features: ["Everything in Launch", "Hotels, activities & transfers", "Multi-currency pricing", "Agent channel & analytics"],
    featured: true,
    tid: "pricing-cta-grow",
  },
  {
    name: "Scale",
    tag: "For multi-brand, multi-country and advanced distribution requirements.",
    features: ["Everything in Grow", "Multi-brand operations", "Custom integrations", "Priority support & SLAs"],
    featured: false,
    tid: "pricing-cta-scale",
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="border-y border-sand bg-parch py-24 lg:py-32">
      <div className="container-x">
        <Reveal><Chapter index={10} label="Pricing" /></Reveal>
        <div className="mt-8 max-w-2xl">
          <Reveal>
            <h2 className="font-serif text-3xl leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              A Platform That Grows With Your Business
            </h2>
            <p className="mt-5 text-base leading-relaxed text-stone2">
              Pricing can be tailored to your market, number of destinations, users
              and integrations — tell us about your business and we'll shape a plan.
            </p>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {PLANS.map((p, i) => (
            <Reveal key={p.name} delay={0.09 * i}>
              <div
                data-testid={`pricing-card-${p.name.toLowerCase()}`}
                className={`flex h-full flex-col rounded-2xl border p-8 transition-all duration-500 hover:-translate-y-1.5 ${
                  p.featured
                    ? "border-ink bg-ink text-paper shadow-[0_40px_70px_-35px_rgba(11,31,51,0.55)]"
                    : "border-sand bg-white hover:border-ink/30"
                }`}
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra">{p.name}</p>
                <p className={`mt-3 font-serif text-2xl ${p.featured ? "text-paper" : "text-ink"}`}>{p.tag}</p>
                <ul className="mt-7 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2.5 text-sm ${p.featured ? "text-paper/75" : "text-stone2"}`}>
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-terra" : "text-emerald2"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={scrollToId("#demo")}
                  data-testid={p.tid}
                  className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-300 ${
                    p.featured
                      ? "bg-terra text-ink hover:bg-paper"
                      : "border border-ink/20 text-ink hover:bg-ink hover:text-paper"
                  }`}
                >
                  Book a Demo
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-center text-sm text-stone2" data-testid="pricing-disclaimer">
            Country-specific pricing is available in dedicated sales brochures for{" "}
            <a href="/brochures/india" data-testid="pricing-brochure-india" className="font-medium text-terra underline underline-offset-4">India</a>,{" "}
            <a href="/brochures/uae" data-testid="pricing-brochure-uae" className="font-medium text-terra underline underline-offset-4">UAE</a> and{" "}
            <a href="/brochures/southeast-asia" data-testid="pricing-brochure-southeast-asia" className="font-medium text-terra underline underline-offset-4">Southeast Asia</a>.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
